import { useGetPayoutsPairsQuery } from "api/TenantSlug/Payout/hooks";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell.tsx";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import { MoneyCell } from "components/UI/Table/UI/MoneyCell/MoneyCell.tsx";
import { last30DaysCreatedAtStr } from "components/UI/Table/utils/getDefaultCreatedAtFilter";
import dayjs from "dayjs";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { usePermission } from "hooks/usePermissions";
import { PayoutResult, PermissionEnum } from "types/enums.ts";

import { COMPACT_DAYS } from "../shared/constants";
import { SearchableTable } from "../shared/SearchableTable";

type PayoutPairData = App.Data.PayoutPairData;

export const LatestTransactions: React.FC = () => {
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tR } = usePaldockTranslation("ui.report.columns");
    const permissionUtils = usePermission();
    const canViewForeignPayout = permissionUtils.hasPermission(PermissionEnum.PayoutViewForeign);

    const requestInclude = canViewForeignPayout
        ? "user,conversionState,conversionState.owner,conversionState.advertiser,conversionState.offer,conversionState.country"
        : "user,conversionState,conversionState.owner,conversionState.offer,conversionState.country";

    const q = useGetPayoutsPairsQuery({
        page: {
            number: 1,
            size: COMPACT_DAYS,
        },
        filter: {
            filter: {
                result: {
                    in: [PayoutResult.Approved],
                },
                created_at: { btw: last30DaysCreatedAtStr },
            },
            sort: "-created_at",
            include: requestInclude,
        },
    });

    const rows = q.data?.payoutPairs || [];

    return (
        <SearchableTable<PayoutPairData>
            rows={rows}
            loading={q.isLoading}
            rowKey={(r) => r.first.id}
            allowSearch={false}
            allowSuffix={false}
            searchKeys={[
                (r) => (r.first.created_at ? dayjs(r.first.created_at).format("DD.MM.YYYY") : ""),
                (r) => r.first.conversionState?.owner?.nickname ?? "",
                (r) => r.first.conversionState?.type ?? "",
                (r) => r.user?.formatted_amount ?? "",
                ...(canViewForeignPayout
                    ? [
                          (r: PayoutPairData) => r.tenant?.formatted_amount ?? "",
                          (r: PayoutPairData) => {
                              const revenue = r.tenant?.amount || 0;
                              const payout = r.user?.amount || 0;
                              return String(revenue - payout);
                          },
                      ]
                    : []),
            ]}
            expandable={{
                childrenColumnName: "none",
            }}
            columns={[
                {
                    title: tG("date"),
                    dataIndex: ["first", "created_at"],
                    render: (v: string) => (v ? dayjs(v).format("DD.MM.YYYY") : <EmptyCell />),
                },
                {
                    title: tG("affiliate"),
                    dataIndex: ["first", "conversionState", "owner", "nickname"],
                    render: (_: unknown, pair: PayoutPairData) => {
                        const owner = pair.first.conversionState?.owner;
                        if (owner) {
                            return <EntityLinkCell.Owner entity={owner} mode="drawer" />;
                        } else {
                            return <EmptyCell />;
                        }
                    },
                },
                {
                    title: tG("type"),
                    dataIndex: ["first", "type"],
                    render: (_: unknown, pair: PayoutPairData) => {
                        return pair.first.conversionState?.type ?? <EmptyCell />;
                    },
                },
                ...(canViewForeignPayout
                    ? [
                          {
                              title: tR("revenue"),
                              dataIndex: ["tenant", "amount"],
                              render: (_: string, pair: PayoutPairData) => {
                                  const tenantPayout = pair.tenant;
                                  return tenantPayout ? (
                                      <MoneyCell
                                          money={{
                                              amount: tenantPayout.amount,
                                              currency_id: tenantPayout.currency_id,
                                              formatted_amount: tenantPayout.formatted_amount,
                                          }}
                                      />
                                  ) : (
                                      <EmptyCell />
                                  );
                              },
                          },
                          {
                              title: tR("cost"),
                              dataIndex: ["user", "amount"],
                              render: (_: string, pair: PayoutPairData) => {
                                  const userPayout = pair.user;
                                  return userPayout ? (
                                      <MoneyCell
                                          money={{
                                              amount: userPayout.amount,
                                              currency_id: userPayout.currency_id,
                                              formatted_amount: userPayout.formatted_amount,
                                          }}
                                      />
                                  ) : (
                                      <EmptyCell />
                                  );
                              },
                          },
                          {
                              title: tR("profit"),
                              dataIndex: "profit",
                              render: (_: string, pair: PayoutPairData) => {
                                  const revenue = pair.tenant?.amount || 0;
                                  const payout = pair.user?.amount || 0;
                                  const profit = revenue - payout;
                                  return (
                                      <MoneyCell
                                          money={{
                                              amount: profit,
                                              currency_id: pair.tenant?.currency_id ?? pair.user?.currency_id ?? 0,
                                              formatted_amount: String(profit),
                                          }}
                                      />
                                  );
                              },
                          },
                      ]
                    : [
                          {
                              title: tG("commission"),
                              dataIndex: ["user", "amount"],
                              render: (_: string, pair: PayoutPairData) => {
                                  const userPayout = pair.user;
                                  return userPayout ? (
                                      <MoneyCell
                                          money={{
                                              amount: userPayout.amount,
                                              currency_id: userPayout.currency_id,
                                              formatted_amount: userPayout.formatted_amount,
                                          }}
                                      />
                                  ) : (
                                      <EmptyCell />
                                  );
                              },
                          },
                      ]),
            ]}
            maxRows={COMPACT_DAYS}
            compact
        />
    );
};
